import {
  BookUser,
  ClipboardCheck,
  FileCheck,
  FileText,
  House,
  MessagesSquare,
  Plug,
  Plus,
  Route as RouteIcon,
  ShieldCheck,
  Target,
  Umbrella,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { brandStyle } from '../../lib/brand';
import { href, type Route } from '../../lib/router';
import { useSession, can } from '../../lib/session';
import { AccountMenu } from '../../ui/AccountMenu';
import { industryLabel } from '../assessments/AssessmentsPage';
import { useConversations } from '../messages/api';
import { useBusiness, useOwnsBusiness } from './context';
import { useStartCheck } from './onboarding';
import './business.css';

interface NavItem {
  label: string;
  icon: LucideIcon;
  to: string;
  on: Route['name'][];
  /** A count beside the label (unread messages). */
  count?: number;
}

/** A top place and the pages inside it, shown as tabs under the bar when there are several. */
interface NavGroup extends NavItem {
  pages: NavItem[];
}

/**
 * The business owner's app, laid out as the prototype's CyberCheck: a top bar with pill navigation over a
 * lighter theme. Every page is about one assessment, the newest unless another is picked.
 */
export function BusinessShell({ route, children }: { route: Route | null; children: ReactNode }) {
  const { me, tenant, signOut } = useSession();
  const { current, assessments, choose } = useBusiness();
  const own = useOwnsBusiness();
  const admin = can(tenant, 'people.view');
  const start = useStartCheck();
  const unread = (useConversations().data ?? []).reduce((n, c) => n + c.unread, 0);

  // Opening one of the business's assessments makes it the one every page shows.
  const routeId = route && 'id' in route ? route.id : null;
  useEffect(() => {
    if (routeId && routeId !== current?.id && assessments.some((a) => a.id === routeId)) choose(routeId);
  }, [routeId, current?.id, assessments, choose]);

  // A new page starts at the top, as a new document would.
  const page = route?.name ?? null;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  // Four places, one per job (task 104, decision 0074): see where you stand, answer, fix, and use it with
  // an insurer. Evidence and connections are part of answering; missions and the plan are both fixing.
  const check = current ? href.assessment(current.id) : href.assessments();
  const group = (label: string, icon: LucideIcon, pages: NavItem[]): NavGroup => ({ label, icon, to: pages[0]!.to, on: pages.flatMap((p) => p.on), pages }); // prettier-ignore
  const nav: NavGroup[] = [
    group('Home', House, [{ label: 'Home', icon: House, to: href.home(), on: ['home', 'score'] }]),
    group('Cyber Check', ClipboardCheck, [
      { label: 'Questions', icon: ClipboardCheck, to: check, on: ['assessments', 'assessment', 'answer', 'chat'] },
      { label: 'Evidence', icon: FileCheck, to: href.locker(), on: ['locker', 'documents'] },
      { label: 'Connections', icon: Plug, to: href.connections(), on: ['connections'] },
    ]),
    group('Fix plan', RouteIcon, [
      { label: '90-day plan', icon: RouteIcon, to: href.plan(), on: ['plan'] },
      { label: 'Missions', icon: Target, to: href.missions(), on: ['missions'] },
    ]),
    group('Insurance', Umbrella, [
      { label: 'Readiness', icon: Umbrella, to: href.insurance(), on: ['insurance'] },
      { label: 'Passport', icon: BookUser, to: href.passport(), on: ['passport'] },
    ]),
    { ...group('Messages', MessagesSquare, [{ label: 'Messages', icon: MessagesSquare, to: href.messages(null), on: ['messages'] }]), count: unread }, // prettier-ignore
  ];
  const here = route ? nav.find((g) => g.on.includes(route.name)) : undefined;
  const name = me?.user.name ?? me?.user.email ?? '';

  return (
    <div className="biz" style={brandStyle(tenant)}>
      <header className="topbar">
        <a className="biz-brand" href={href.home()}>
          <span className="biz-mark">
            <ShieldCheck size={19} />
          </span>
          <span>
            CyberScore<small>CyberCheck</small>
          </span>
        </a>
        <nav className="pillnav" aria-label="Main">
          <div className="pillnav-inner">
            {nav.map((n) => (
              <a key={n.label} href={n.to} aria-current={route && n.on.includes(route.name) ? 'page' : undefined}>
                <n.icon size={17} />
                <span>{n.label}</span>
                {!!n.count && (
                  <span className="nav-count" aria-label={`${n.count} unread`}>
                    {n.count > 99 ? '99+' : n.count}
                  </span>
                )}
              </a>
            ))}
          </div>
        </nav>
        <AccountMenu name={name} org={current?.customer_name ?? tenant?.name ?? null} onSignOut={signOut}>
          {current && (
            <a className="menu-item" role="menuitem" href={href.report()}>
              <FileText size={14} /> Your CyberScore report
            </a>
          )}
          {admin && (
            <a className="menu-item" role="menuitem" href={href.people()}>
              <Users size={14} /> Your team
            </a>
          )}
          {own && (
            <button type="button" className="menu-item" role="menuitem" disabled={start.isPending} onClick={() => start.mutate()}>
              <Plus size={14} /> Start a new Cyber Check
            </button>
          )}
          {(current || own || admin) && <div className="menu-sep" />}
          {assessments.length > 1 && (
            <>
              <div className="menu-label">Assessment</div>
              {assessments.slice(0, 12).map((a) => (
                <button key={a.id} type="button" role="menuitemradio" aria-checked={a.id === current?.id} className="menu-item" onClick={() => choose(a.id)}>
                  <span className="grow">
                    {a.customer_name}
                    <span className="muted small"> · {a.title}</span>
                  </span>
                </button>
              ))}
              <a className="menu-item" href={href.assessments()}>
                All assessments ({assessments.length})
              </a>
              <div className="menu-sep" />
            </>
          )}
          {current && (
            <div className="menu-foot muted small">
              {current.customer_name} · {industryLabel(current.context.industry)}
            </div>
          )}
        </AccountMenu>
      </header>
      <main className="biz-page">
        {here && here.pages.length > 1 && (
          <nav className="subnav" aria-label={here.label}>
            {here.pages.map((p) => (
              <a key={p.label} href={p.to} aria-current={route && p.on.includes(route.name) ? 'page' : undefined}>
                <p.icon size={15} />
                <span>{p.label}</span>
              </a>
            ))}
          </nav>
        )}
        {children}
      </main>
    </div>
  );
} // prettier-ignore
